import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, useMap, Circle, CircleMarker, Pane, Polyline } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { Locate, MapPin } from 'lucide-react';

import MapBottomSheet from '../components/MapBottomSheet';
import { useEstablishments } from '../hooks/useData';
import type { Establishment, EstablishmentMapPoint } from '../hooks/useData';
import { useUserGeolocation, GEOLOCATION_OPTIONS, type UserGeoFix } from '../hooks/useUserGeolocation';
import { getCategoryPinMarkup, getCategoryPreset, normalizeCategorySlug } from '../utils/categoryPresets';
import './MapPage.css';

/** Instância do OSM Buildings (script carregado via index.html) */
interface OSMBuildingsInstance {
    load: (url?: string) => void;
    style: (opts: { color?: string; roofColor?: string; shadows?: boolean }) => void;
}

declare global {
    interface Window {
        OSMBuildings: new (map: L.Map) => OSMBuildingsInstance;
    }
}

// ─── Pins do mapa: mesmos ícones/cores que a Home (`getCategoryPinMarkup`) ───
const createCategoryIcon = (categorySlug?: string, categoryLabel?: string) => {
    const { color, svg } = getCategoryPinMarkup(categorySlug ?? '', categoryLabel ?? '');

    return L.divIcon({
        html: `<div class="custom-pin" style="--pin-color: ${color}">
            <div class="custom-pin-inner">${svg}</div>
        </div>`,
        className: 'custom-marker-wrapper',
        iconSize: [52, 64],
        iconAnchor: [26, 64],
    });
};

// ─── Camada de silhuetas dos edifícios (estilo Google Maps) ───
const OSMBuildingsLayer = () => {
    const map = useMap();
    const osmbRef = useRef<OSMBuildingsInstance | null>(null);

    useEffect(() => {
        if (typeof window.OSMBuildings === 'undefined') return;

        const OSMB = window.OSMBuildings;
        const osmb = new OSMB(map);
        osmbRef.current = osmb;

        // Estilo discreto: tons neutros para não competir com os marcadores
        osmb.style({
            color: 'rgba(220, 218, 213, 0.85)',
            roofColor: 'rgba(200, 198, 193, 0.9)',
            shadows: false,
        });

        // Tiles oficiais OSM Buildings (dados de edificações)
        osmb.load('https://{s}.data.osmbuildings.org/0.2/59fcc2e8/tile/{z}/{x}/{y}.json');

        return () => {
            osmbRef.current = null;
            // Leaflet não expõe remove fácil do OSMBuildings; o layer some ao desmontar o mapa
        };
    }, [map]);

    return null;
};

/** Círculo de incerteza + ponto “eu estou aqui” (fix vem do hook no pai — uma única watch). */
const UserLocationOnMap = ({ fix }: { fix: UserGeoFix | null }) => {
    if (!fix) return null;

    const center: [number, number] = [fix.lat, fix.lng];

    return (
        <>
            <Circle
                center={center}
                radius={fix.accuracyM}
                pathOptions={{
                    className: 'map-user-accuracy',
                    color: '#1a73e8',
                    fillColor: '#1a73e8',
                    fillOpacity: 0.14,
                    weight: 1,
                    opacity: 0.85,
                }}
            />
            <CircleMarker
                center={center}
                radius={8}
                pathOptions={{
                    className: 'map-user-dot',
                    color: '#ffffff',
                    fillColor: '#1a73e8',
                    fillOpacity: 1,
                    weight: 3,
                }}
            />
        </>
    );
};

// ─── My Location Button ───
const LocationButton = () => {
    const map = useMap();

    const handleClick = useCallback(() => {
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const { latitude, longitude } = pos.coords;
                map.flyTo([latitude, longitude], 16, { duration: 1.2 });
            },
            () => alert('Não foi possível obter sua localização. Verifique se a localização está ativada para este site no iPhone ou Android.'),
            GEOLOCATION_OPTIONS,
        );
    }, [map]);

    return (
        <button type="button" className="my-location-btn" onClick={handleClick} aria-label="Minha localização">
            <Locate size={20} />
        </button>
    );
};

/** Caminho a pé via OSM (serviço público OSRM; só para visualização no mapa). */
async function fetchOsrmWalkingRoute(
    from: { lat: number; lng: number },
    to: { lat: number; lng: number },
): Promise<[number, number][] | null> {
    const url =
        `https://router.project-osrm.org/route/v1/walking/${from.lng},${from.lat};${to.lng},${to.lat}` +
        '?overview=full&geometries=geojson';
    try {
        const res = await fetch(url);
        if (!res.ok) return null;
        const json = await res.json();
        const coords = json.routes?.[0]?.geometry?.coordinates as [number, number][] | undefined;
        if (!coords?.length) return null;
        return coords.map(([lng, lat]) => [lat, lng] as [number, number]);
    } catch {
        return null;
    }
}

/** Voa até o alvo (ex.: deep link do assistente). */
function MapFlyTo({
    target,
    onDone,
}: {
    target: [number, number] | null;
    onDone: () => void;
}) {
    const map = useMap();
    useEffect(() => {
        if (!target) return;
        map.flyTo(target, 16, { duration: 0.85 });
        const t = window.setTimeout(onDone, 950);
        return () => window.clearTimeout(t);
    }, [map, target, onDone]);
    return null;
}

function MapRoutePolyline({ positions }: { positions: [number, number][] | null }) {
    const map = useMap();

    useEffect(() => {
        if (!positions?.length) return;
        const bounds = L.latLngBounds(positions);
        map.fitBounds(bounds, { padding: [56, 56], maxZoom: 15, animate: true });
    }, [map, positions]);

    if (!positions?.length) return null;

    return (
        <Polyline
            positions={positions}
            pathOptions={{
                color: '#1a73e8',
                weight: 5,
                opacity: 0.9,
                lineJoin: 'round',
                lineCap: 'round',
            }}
        />
    );
}

// Ubatuba coords
const UBATUBA_CENTER: [number, number] = [-23.4332, -45.0703];

/** Chave única da categoria no mapa (id da API, slug ou rótulo). */
function placeCategoryFilterKey(p: Establishment): string {
    const id = p.category?.id ?? p.categoryId ?? '';
    if (id) return String(id);
    const slug = normalizeCategorySlug(p.category?.slug ?? '');
    if (slug) return slug;
    const label = (p.category?.label ?? 'Outros').trim() || 'Outros';
    return `_n:${label}`;
}

type MapCategoryChip = {
    filterKey: string;
    slug: string;
    label: string;
    count: number;
};

const MapPage = () => {
    const { data: establishments } = useEstablishments();
    const userFix = useUserGeolocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [categoryFilter, setCategoryFilter] = useState<string>('all');
    const [selectedPlace, setSelectedPlace] = useState<EstablishmentMapPoint | null>(null);
    const [routePath, setRoutePath] = useState<[number, number][] | null>(null);
    const [flyTarget, setFlyTarget] = useState<[number, number] | null>(null);
    const deepLinkHandledKey = useRef('');

    const geoPlaces = useMemo(
        () => establishments.filter((e) => e.latitude != null && e.longitude != null),
        [establishments],
    );

    const mapCategoryChips = useMemo((): MapCategoryChip[] => {
        const m = new Map<string, { slug: string; label: string; count: number }>();
        for (const p of geoPlaces) {
            const key = placeCategoryFilterKey(p);
            const slug = p.category?.slug ?? '';
            const label = (p.category?.label ?? 'Outros').trim() || 'Outros';
            const cur = m.get(key);
            if (cur) cur.count += 1;
            else m.set(key, { slug, label, count: 1 });
        }
        return Array.from(m.entries())
            .map(([filterKey, v]) => ({ filterKey, ...v }))
            .sort((a, b) => a.label.localeCompare(b.label, 'pt'));
    }, [geoPlaces]);

    useEffect(() => {
        if (categoryFilter === 'all') return;
        const stillThere = mapCategoryChips.some((c) => c.filterKey === categoryFilter);
        if (!stillThere) {
            queueMicrotask(() => setCategoryFilter('all'));
        }
    }, [categoryFilter, mapCategoryChips]);

    const places = useMemo((): EstablishmentMapPoint[] => {
        let filtered: Establishment[] = geoPlaces;
        if (categoryFilter !== 'all') {
            filtered = geoPlaces.filter((p) => placeCategoryFilterKey(p) === categoryFilter);
        }
        return filtered.map((p) => ({
            ...p,
            lat: p.latitude as number,
            lng: p.longitude as number,
        }));
    }, [geoPlaces, categoryFilter]);

    /** Mantém o bottom sheet alinhado ao Realtime quando lat/lng/endereço mudam no servidor. */
    useEffect(() => {
        if (selectedPlace == null) return;
        const fresh = places.find((p) => p.id === selectedPlace.id);
        if (!fresh) return;
        if (
            fresh.lat === selectedPlace.lat &&
            fresh.lng === selectedPlace.lng &&
            fresh.address === selectedPlace.address
        ) {
            return;
        }
        setSelectedPlace(fresh);
    }, [places, selectedPlace]);

    const handleRouteInApp = useCallback(async (to: { lat: number; lng: number }) => {
        setSelectedPlace(null);
        await new Promise<void>((resolve) => {
            navigator.geolocation.getCurrentPosition(
                async (pos) => {
                    const from = {
                        lat: pos.coords.latitude,
                        lng: pos.coords.longitude,
                    };
                    const path = await fetchOsrmWalkingRoute(from, to);
                    if (path?.length) {
                        setRoutePath(path);
                    } else {
                        alert('Não foi possível traçar a rota no mapa. Tente abrir no Google Maps ou no Waze.');
                    }
                    resolve();
                },
                () => {
                    alert('Ative a localização do navegador para traçar a rota neste mapa.');
                    resolve();
                },
                GEOLOCATION_OPTIONS,
            );
        });
    }, []);

    const focusId = searchParams.get('focus');
    const wantRouteFromQuery = searchParams.get('route') === '1';

    useEffect(() => {
        if (!focusId) {
            deepLinkHandledKey.current = '';
            return;
        }
        if (!geoPlaces.length) return;

        const est = geoPlaces.find((e) => e.id === focusId);
        if (
            est == null ||
            est.latitude == null ||
            est.longitude == null ||
            !Number.isFinite(est.latitude) ||
            !Number.isFinite(est.longitude)
        ) {
            const next = new URLSearchParams(searchParams);
            next.delete('focus');
            next.delete('route');
            setSearchParams(next, { replace: true });
            return;
        }

        const key = `${focusId}:${wantRouteFromQuery}`;
        if (deepLinkHandledKey.current === key) return;
        deepLinkHandledKey.current = key;

        const pt: EstablishmentMapPoint = {
            ...est,
            lat: est.latitude,
            lng: est.longitude,
        };

        if (wantRouteFromQuery) {
            void handleRouteInApp({ lat: pt.lat, lng: pt.lng });
        } else {
            setFlyTarget([pt.lat, pt.lng]);
            setSelectedPlace(pt);
        }

        const next = new URLSearchParams(searchParams);
        next.delete('focus');
        next.delete('route');
        setSearchParams(next, { replace: true });
    }, [focusId, wantRouteFromQuery, geoPlaces, handleRouteInApp, searchParams, setSearchParams]);

    const clearFlyTarget = useCallback(() => setFlyTarget(null), []);

    return (
        <div className="map-page">
            {/* Header Overlay */}
            <div className="map-header-overlay">
                <h3>Explorar Mapa</h3>
                <div className="map-filters">
                    <button
                        type="button"
                        key="all"
                        className={`map-chip ${categoryFilter === 'all' ? 'active' : ''}`}
                        onClick={() => setCategoryFilter('all')}
                    >
                        <MapPin size={14} />
                        <span>Todos</span>
                        {categoryFilter === 'all' && places.length > 0 && (
                            <span className="chip-count">{places.length}</span>
                        )}
                    </button>
                    {mapCategoryChips.map((c) => {
                        const Icon = getCategoryPreset(c.slug, c.label).icon;
                        const active = categoryFilter === c.filterKey;
                        return (
                            <button
                                type="button"
                                key={c.filterKey}
                                className={`map-chip ${active ? 'active' : ''}`}
                                onClick={() => setCategoryFilter(c.filterKey)}
                            >
                                <Icon size={14} />
                                <span>{c.label}</span>
                                {active && places.length > 0 && (
                                    <span className="chip-count">{places.length}</span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Map */}
            <MapContainer
                center={UBATUBA_CENTER}
                zoom={15}
                style={{ width: '100%', height: '100%' }}
                zoomControl={false}
            >
                <TileLayer
                    url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
                    maxZoom={20}
                />
                <OSMBuildingsLayer />
                {places.map(place => (
                    <Marker
                        key={`${place.id}-${place.lat}-${place.lng}`}
                        position={[place.lat, place.lng]}
                        icon={createCategoryIcon(place.category?.slug, place.category?.label)}
                        eventHandlers={{
                            click: () => setSelectedPlace(place),
                        }}
                    />
                ))}
                <Pane name="facilitaUserLocation" style={{ zIndex: 620 }}>
                    <UserLocationOnMap fix={userFix} />
                </Pane>
                <Pane name="facilitaRoute" style={{ zIndex: 610 }}>
                    <MapRoutePolyline positions={routePath} />
                </Pane>
                <MapFlyTo target={flyTarget} onDone={clearFlyTarget} />
                <LocationButton />
            </MapContainer>

            {routePath && routePath.length > 0 && (
                <button
                    type="button"
                    className="clear-route-btn"
                    onClick={() => setRoutePath(null)}
                >
                    Limpar rota
                </button>
            )}

            {/* BottomSheet só monta com local selecionado — evita inconsistência de hooks com place null */}
            {selectedPlace != null && (
                <MapBottomSheet
                    place={selectedPlace}
                    userLocation={userFix ? { lat: userFix.lat, lng: userFix.lng } : null}
                    onClose={() => setSelectedPlace(null)}
                    onRouteInApp={handleRouteInApp}
                />
            )}
        </div>
    );
};

export default MapPage;
